import React, { useState, useMemo } from 'react';

const PlaceholderLogo = ({ name }) => (
  <div style={{
    width: '100%',
    height: '30px',
    backgroundColor: '#f0f0f0',
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: '#000'
  }}>
    {name}
  </div>
);

const NPSDriversVisualization = ({ data, logoMap }) => {
  // Sort data to ensure Grab is first
  const sortedData = useMemo(() => {
    const grabIndex = data.findIndex(brand => brand.name.toLowerCase().includes('grab'));
    if (grabIndex === -1) return data;
    
    const newData = [...data];
    const grabItem = newData.splice(grabIndex, 1)[0];
    return [grabItem, ...newData];
  }, [data]);

  const [selectedBrand, setSelectedBrand] = useState(0);
  const [sortBy, setSortBy] = useState('freq'); // 'freq' or 'label'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  // Extract unique drivers from the data
  const drivers = useMemo(() => {
    if (!sortedData.length) return [];
    return [...new Set(sortedData.flatMap(brand => 
      brand.funnelData.map(item => item.label)
    ))];
  }, [sortedData]);

  // Prepare data for visualization
  const chartData = useMemo(() => {
    if (!sortedData.length) return [];
    
    return drivers.map(driver => {
      const brandFrequencies = sortedData.map(brand => {
        const item = brand.funnelData.find(d => d.label === driver);
        return item ? item.freq : 0;
      });
      
      return {
        driver,
        frequencies: brandFrequencies,
        average: brandFrequencies.reduce((sum, val) => sum + val, 0) / brandFrequencies.length
      };
    });
  }, [sortedData, drivers]);

  // Sort the data based on selected criteria
  const sortedChartData = useMemo(() => {
    return [...chartData].sort((a, b) => {
      let valueA, valueB;
      
      if (sortBy === 'freq') {
        valueA = a.frequencies[selectedBrand];
        valueB = b.frequencies[selectedBrand];
      } else {
        valueA = a.driver;
        valueB = b.driver;
      }
      
      if (sortOrder === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });
  }, [chartData, selectedBrand, sortBy, sortOrder]);

  // Find the maximum frequency for scaling
  const maxFrequency = useMemo(() => {
    const allFrequencies = chartData.flatMap(d => d.frequencies);
    return Math.max(...allFrequencies, 1); // Ensure at least 1 for scaling
  }, [chartData]);

  // Color palette for brands
  const brandColors = sortedData.map(brand => brand.color || '#3b82f6');

  return (
    <div style={{
      padding: '24px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      maxWidth: '1400px',
      margin: '0 auto',
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    }}>


      {/* Brand Selection */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '16px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ 
          color: '#1e293b', 
          margin: '0 0 16px 0', 
          fontSize: '18px',
          fontWeight: '600'
        }}>Select Brand:</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {sortedData.map((brand, index) => (
            <button
              key={index}
              onClick={() => setSelectedBrand(index)}
              style={{
                padding: '12px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: index === selectedBrand ? brandColors[index] : '#f1f5f9',
                color: index === selectedBrand ? 'white' : '#64748b',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                minWidth: '120px',
                boxShadow: index === selectedBrand ? '0 4px 6px rgba(0,0,0,0.1)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = index === selectedBrand ? brandColors[index] : '#e2e8f0';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = index === selectedBrand ? brandColors[index] : '#f1f5f9';
              }}
            >
              {brand.name}
            </button>
          ))}
        </div>
      </div>

      {/* Sort Controls */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '16px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap'
      }}>
        <span style={{ color: '#64748b', fontWeight: '500' }}>Sort by:</span>
        
        <button
          onClick={() => {
            setSortBy('freq');
            setSortOrder(sortBy === 'freq' ? (sortOrder === 'desc' ? 'asc' : 'desc') : 'desc');
          }}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: sortBy === 'freq' ? '#3b82f6' : '#f1f5f9',
            color: sortBy === 'freq' ? 'white' : '#64748b',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          Frequency
          {sortBy === 'freq' && (
            <span>{sortOrder === 'desc' ? '↓' : '↑'}</span>
          )}
        </button>
        
        <button
          onClick={() => {
            setSortBy('label');
            setSortOrder(sortBy === 'label' ? (sortOrder === 'desc' ? 'asc' : 'desc') : 'asc');
          }}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: sortBy === 'label' ? '#3b82f6' : '#f1f5f9',
            color: sortBy === 'label' ? 'white' : '#64748b',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          Driver Name
          {sortBy === 'label' && (
            <span>{sortOrder === 'desc' ? '↓' : '↑'}</span>
          )}
        </button>
      </div>

      {/* Comparison Chart */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0',
        marginBottom: '24px'
      }}>
        <h2 style={{
          color: '#1e293b',
          margin: '0 0 24px 0',
          fontSize: '20px',
          fontWeight: '600'
        }}>
          Brand Comparison - NPS Level 1 Drivers
        </h2>

        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr>
                <th style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  borderBottom: '2px solid #e2e8f0',
                  color: '#64748b',
                  fontWeight: '600',
                  fontSize: '14px',
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'white',
                  zIndex: 1
                }}>
                  Driver
                </th>
                {sortedData.map((brand, index) => (
                  <th key={index} style={{
                    textAlign: 'center',
                    padding: '12px 16px',
                    borderBottom: '2px solid #e2e8f0',
                    color: '#64748b',
                    fontWeight: '600',
                    fontSize: '14px',
                    minWidth: '100px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}>
                      {logoMap[brand.logo] || <PlaceholderLogo name={brand.name} />}
                      {brand.name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedChartData.map((item, index) => (
                <tr key={index} style={{
                  backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <td style={{
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#1e293b',
                    fontWeight: '500',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white',
                    zIndex: 1
                  }}>
                    {item.driver}
                  </td>
                  {sortedData.map((brand, brandIndex) => {
                    const frequency = item.frequencies[brandIndex];
                    return (
                      <td key={brandIndex} style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: frequency > 0 ? '#1e293b' : '#94a3b8'
                      }}>
                        {frequency > 0 ? frequency : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Main Visualization */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        marginBottom: '30px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{
          color: '#1e293b',
          margin: '0 0 24px 0',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: brandColors[selectedBrand],
            borderRadius: '4px'
          }}></div>
          {sortedData[selectedBrand]?.name} - NPS Level 1 Drivers
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: '16px'
        }}>
          {/* Drivers list */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {sortedChartData.map((item, index) => {
              const frequency = item.frequencies[selectedBrand];
              return (
                <div
                  key={index}
                  style={{
                    padding: '16px',
                    borderRadius: '8px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                  }}
                >
                  <span style={{
                    color: '#1e293b',
                    fontSize: '14px',
                    fontWeight: '500',
                    flex: 1
                  }}>
                    {item.driver}
                  </span>
                  
                  <div style={{
                    width: '60px',
                    textAlign: 'right',
                    color: frequency > 0 ? '#1e293b' : '#94a3b8',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    {frequency > 0 ? frequency : 'N/A'}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bar chart visualization */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '400px'
          }}>
            {sortedChartData.map((item, index) => {
              const frequency = item.frequencies[selectedBrand];
              return (
                <div
                  key={index}
                  style={{
                    height: '52px',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative'
                  }}
                >
                  {frequency > 0 ? (
                    <div
                      style={{
                        width: `${(frequency / maxFrequency) * 100}%`,
                        height: '24px',
                        backgroundColor: brandColors[selectedBrand],
                        borderRadius: '4px',
                        transition: 'width 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: '8px',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '12px',
                        minWidth: '40px'
                      }}
                    >
                      {frequency > 0 && frequency}
                    </div>
                  ) : (
                    <div style={{
                      color: '#94a3b8',
                      fontSize: '12px',
                      fontStyle: 'italic'
                    }}>
                      No data available
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Information Section
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{
          color: '#1e293b',
          margin: '0 0 16px 0',
          fontSize: '18px',
          fontWeight: '600'
        }}>About NPS Level 1 Drivers</h3>
        
        <p style={{
          color: '#64748b',
          margin: '0 0 20px 0',
          fontSize: '14px',
          lineHeight: '1.6'
        }}>
          NPS Level 1 Drivers represent the key factors that influence customer satisfaction and loyalty. 
          The frequency values indicate how often each driver was mentioned as a reason for satisfaction or dissatisfaction.
        </p>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px'
        }}>
          <div>
            <h4 style={{
              color: '#3b82f6',
              margin: '0 0 8px 0',
              fontSize: '16px',
              fontWeight: '600'
            }}>Customer Journey</h4>
            <p style={{
              color: '#64748b',
              margin: '0',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              Drivers like Registration, Search, Order Process, and Delivery represent different stages of the customer journey.
            </p>
          </div>
          
          <div>
            <h4 style={{
              color: '#3b82f6',
              margin: '0 0 8px 0',
              fontSize: '16px',
              fontWeight: '600'
            }}>Value Perception</h4>
            <p style={{
              color: '#64748b',
              margin: '0',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              Price, promotions, and app quality influence how customers perceive the value they receive.
            </p>
          </div>
          
          <div>
            <h4 style={{
              color: '#3b82f6',
              margin: '0 0 8px 0',
              fontSize: '16px',
              fontWeight: '600'
            }}>Brand Factors</h4>
            <p style={{
              color: '#64748b',
              margin: '0',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              Company reputation and customer service play crucial roles in building trust and loyalty.
            </p>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default NPSDriversVisualization;